// import { Plus } from "lucide-react";
import { z } from "zod";
import Cookies from "js-cookie";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MdClose } from "react-icons/md";

import {
  AlertDialog,
  AlertDialogContent,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/command";
import { Input } from "@/components/ui/input/input";
import { editPerformanceReport } from "@/config/mutation/admin";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { useEditPreviousReportModal } from "./use-edit-previous-report-modal";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon, Check, ChevronsUpDown } from "lucide-react";
import { Separator } from "@/components/ui/separator/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion/accordion";
import { Textarea } from "@/components/ui/textarea/textarea";
import { Calendar } from "@/components/ui/calendar/calendar";
import { format } from "date-fns";
import { Switch } from "@/components/ui/switch";
import PenaltyInput from "./penalty-input";
import { AiOutlineDelete } from "react-icons/ai";

const formSchema = z.object({
  report: z.string(),
  remainingPoints: z.coerce.number().default(0),
  misconducts: z
    .array(
      z.object({
        reason: z.string(),
        dateAdded: z.date(),
        misconductOn: z.date(),
        misconductFor: z.string(),
        misconductingInvolvingOthers: z.boolean().default(false),
        penalties: z.array(z.string()),
      })
    )
    .optional(),
});

export type FormValues = z.infer<typeof formSchema>;

interface EditPreviousMonthReportModalProps {
  previousReports: {
    label: string;
    points: number;
    value: string;
    misconducts: {
      reason: string;
      dateAdded: Date;
      misconductOn: Date;
      misconductFor: string;
      misconductingInvolvingOthers: boolean;
      penalties: string[];
    }[];
  }[];
}

export const EditPreviousMonthReportModal = ({
  previousReports,
}: EditPreviousMonthReportModalProps) => {
  const user = Cookies.get("key") as string;
  const { open, onOpenChange } = useEditPreviousReportModal();
  const queryClient = useQueryClient();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      report: "",
      remainingPoints: 0,
      misconducts: [],
    },
  });

  const selectedReport = form.watch("report");

  const {
    append: appendMisconduct,
    remove: removeMisconduct,
    fields: misconducts,
  } = useFieldArray({
    control: form.control,
    name: "misconducts",
  });

  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn: editPerformanceReport(user),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    mutate(
      {
        reportId: values?.report,
        info: {
          remainingPoints: values?.remainingPoints,
          misconducts: values?.misconducts || [],
        },
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["performance-reports"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["performance-logs"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["performance-agg-points"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["employee"],
          });

          toast.success("Performance report updated!");
          onOpenChange(false);

          form.reset();
        },

        onError: (error) => {
          console.log("ERROR WHILE UPDATING PERFORMANCE REPORT", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="p-0 max-w-3xl">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="border-b px-3 py-2 flex items-center justify-between">
                <h3 className="text-xl font-medium tracking-normal">
                  Edit Previous Month Report
                </h3>
                <div className="relative flex items-center gap-4">
                  <Button size="sm" disabled={isSubmitting}>
                    Save
                  </Button>
                  <button
                    disabled={isSubmitting}
                    type="button"
                    className="p-1 rounded-md transition-all duration-300 hover:bg-accent"
                    onClick={() => {
                      onOpenChange(false);
                      form.reset();
                    }}
                  >
                    <MdClose className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-4 p-5">
                <FormField
                  control={form.control}
                  name="report"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Month</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "w-full justify-between",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value
                                ? previousReports?.find(
                                    (report) => report.value === field.value
                                  )?.label
                                : "Select Month"}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-[200px] p-0">
                          <Command>
                            <CommandInput placeholder="Search month..." />
                            <CommandEmpty>
                              No previous month found.
                            </CommandEmpty>
                            <CommandList>
                              <CommandGroup>
                                {previousReports?.map((report) => (
                                  <CommandItem
                                    value={report.label}
                                    key={report.value}
                                    onSelect={() => {
                                      form.setValue("report", report?.value);
                                      form.setValue(
                                        "remainingPoints",
                                        report?.points
                                      );

                                      const tempMisconduct =
                                        report?.misconducts?.map((item) => {
                                          return {
                                            ...item,
                                            dateAdded: new Date(item.dateAdded),
                                            misconductOn: new Date(
                                              item.misconductOn
                                            ),
                                          };
                                        });
                                      form.setValue(
                                        "misconducts",
                                        tempMisconduct
                                      );
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        report.value === field.value
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    {report.label}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="remainingPoints"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Points</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          disabled={isSubmitting || selectedReport === ""}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {selectedReport && (
                  <>
                    <Separator className="mb-4" />

                    {misconducts?.length > 0 ? (
                      <div className="px-5 flex flex-col gap-3 max-h-[350px] md:max-h-[430px] overflow-y-auto">
                        {misconducts?.map((misconduct, index) => {
                          return (
                            <div
                              className="flex items-center gap-2"
                              key={misconduct?.id}
                            >
                              <Accordion
                                type="single"
                                collapsible
                                className="w-full flex-1"
                              >
                                <AccordionItem
                                  value={`item-${index + 1}`}
                                  className="px-3"
                                >
                                  <AccordionTrigger>
                                    Misconduct {index + 1}
                                  </AccordionTrigger>
                                  <AccordionContent>
                                    <div className="py-1 flex flex-col gap-3">
                                      <div className="space-y-1">
                                        <label
                                          htmlFor={`reason_${index}`}
                                          className="text-xs"
                                        >
                                          Reason
                                        </label>
                                        <Textarea
                                          id={`reason_${index}`}
                                          placeholder="misconduct reason..."
                                          {...form.register(
                                            `misconducts.${index}.reason` as const
                                          )}
                                          rows={2}
                                        />
                                      </div>

                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="space-y-1">
                                          <label
                                            htmlFor={`dateAdded_${index}`}
                                            className="text-xs"
                                          >
                                            Added Date
                                          </label>
                                          <Controller
                                            control={form.control}
                                            name={`misconducts.${index}.dateAdded`}
                                            render={({
                                              field: { onChange, value },
                                            }) => (
                                              <Popover>
                                                <PopoverTrigger asChild>
                                                  <Button
                                                    variant="outline"
                                                    className={cn(
                                                      "col-span-full justify-start text-left font-normal flex items-center gap-2 w-full",
                                                      !value &&
                                                        "text-muted-foreground"
                                                    )}
                                                  >
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {value ? (
                                                      format(value, "PPP")
                                                    ) : (
                                                      <span>Pick a date</span>
                                                    )}
                                                  </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0">
                                                  <Calendar
                                                    mode="single"
                                                    selected={value as Date}
                                                    onSelect={(
                                                      selectedDate
                                                    ) => {
                                                      // Ensure a valid Date is selected before updating.
                                                      if (selectedDate) {
                                                        onChange(selectedDate);
                                                      }
                                                    }}
                                                  />
                                                </PopoverContent>
                                              </Popover>
                                            )}
                                          />
                                        </div>

                                        <div className="space-y-1">
                                          <label
                                            htmlFor={`misconductOn_${index}`}
                                            className="text-xs"
                                          >
                                            Misconduct on
                                          </label>
                                          <Controller
                                            control={form.control}
                                            name={`misconducts.${index}.misconductOn`}
                                            render={({
                                              field: { onChange, value },
                                            }) => (
                                              <Popover>
                                                <PopoverTrigger asChild>
                                                  <Button
                                                    variant="outline"
                                                    className={cn(
                                                      "col-span-full justify-start text-left font-normal flex items-center gap-2 w-full",
                                                      !value &&
                                                        "text-muted-foreground"
                                                    )}
                                                  >
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {value ? (
                                                      format(value, "PPP")
                                                    ) : (
                                                      <span>Pick a date</span>
                                                    )}
                                                  </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0">
                                                  <Calendar
                                                    mode="single"
                                                    selected={value as Date}
                                                    onSelect={(
                                                      selectedDate
                                                    ) => {
                                                      // Ensure a valid Date is selected before updating.
                                                      if (selectedDate) {
                                                        onChange(selectedDate);
                                                      }
                                                    }}
                                                  />
                                                </PopoverContent>
                                              </Popover>
                                            )}
                                          />
                                        </div>
                                      </div>

                                      <div className="space-y-1">
                                        <Controller
                                          control={form.control}
                                          name={`misconducts.${index}.misconductingInvolvingOthers`}
                                          render={({
                                            field: { value, onChange },
                                          }) => (
                                            <div className="flex items-center justify-between p-3 rounded-md">
                                              <div>
                                                <h4 className="text-sm font-semibold">
                                                  Involving Others?
                                                </h4>
                                                <p className="text-xs text-muted-foreground">
                                                  Toggle if this misconduct
                                                  involved other individuals.
                                                </p>
                                              </div>
                                              <Switch
                                                id={`misconductingInvolvingOthers_${index}`}
                                                checked={value}
                                                onCheckedChange={onChange}
                                              />
                                            </div>
                                          )}
                                        />
                                      </div>

                                      <div className="mt-4">
                                        <h4 className="text-xs">Penalties</h4>
                                        <Controller
                                          control={form.control}
                                          name={`misconducts.${index}.penalties`}
                                          defaultValue={[]} // Ensure penalties is an array by default.
                                          render={({
                                            field: { value, onChange },
                                          }) => (
                                            <PenaltyInput
                                              value={value || []}
                                              onChange={onChange}
                                            />
                                          )}
                                        />
                                      </div>
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              </Accordion>

                              <span
                                className="cursor-pointer hover:scale-110 active:scale-95 transition-all"
                                onClick={() => {
                                  removeMisconduct(index);
                                }}
                              >
                                <AiOutlineDelete size={20} />
                              </span>
                            </div>
                          );
                        })}

                        <Button
                          asChild
                          variant="outline"
                          className="cursor-pointer"
                          type="button"
                          onClick={() => {
                            appendMisconduct({
                              reason: "",
                              dateAdded: new Date(),
                              misconductOn: new Date(),
                              misconductFor: "",
                              misconductingInvolvingOthers: false,
                              penalties: [],
                            });
                          }}
                        >
                          <div>Add misconduct</div>
                        </Button>
                      </div>
                    ) : (
                      <div className="px-5 flex items-center justify-center flex-col gap-2">
                        <h3 className="text-lg">No Misconduct</h3>
                        <Button
                          asChild
                          variant="outline"
                          className="cursor-pointer"
                          type="button"
                          onClick={() => {
                            appendMisconduct({
                              reason: "",
                              dateAdded: new Date(),
                              misconductOn: new Date(),
                              misconductFor: "",
                              misconductingInvolvingOthers: false,
                              penalties: [],
                            });
                          }}
                        >
                          <div>Add misconduct</div>
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
